// src/hooks/usePlanAccess.js
import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  CAP,
  INTERNAL_DOMAINS,
  MIN_PLAN_FOR,
  PLAN_CAPABILITIES,
  PLAN_LABELS,
  normalisePlanId,
} from "../config/planAccess";

/**
 * What can the signed-in user do on their plan?
 *   const { can, planLabel, upgradeLabel } = usePlanAccess();
 *   can(CAP.ENRICHMENT)            → boolean
 *   upgradeLabel(CAP.ENRICHMENT)   → "Grow"
 *
 * Superadmins and Peach Loves staff are never restricted (matches backend).
 */
export default function usePlanAccess() {
  const { user } = useAuth();

  return useMemo(() => {
    const domain = (user?.email || "").split("@")[1]?.toLowerCase();
    const unrestricted =
      !!user?.isSuperAdmin || (!!domain && INTERNAL_DOMAINS.includes(domain));

    const planId = normalisePlanId(user?.customer?.planId);
    const caps = new Set(PLAN_CAPABILITIES[planId]);
    const overrides = user?.customer?.features?.plan_capability_overrides;
    if (Array.isArray(overrides)) overrides.forEach((c) => caps.add(c));
    // Legacy per-customer grant for AI Revenue Intelligence.
    if (user?.customer?.features?.revenue_intelligence_enabled) {
      caps.add(CAP.REVENUE_INTELLIGENCE);
    }

    return {
      planId,
      planLabel: PLAN_LABELS[planId],
      can: (capability) => unrestricted || caps.has(capability),
      upgradeLabel: (capability) =>
        PLAN_LABELS[MIN_PLAN_FOR[capability]] || "a higher plan",
    };
  }, [user]);
}
